// ---------------------------------------------------------------------------
// 損益分岐点 可視化 — 自前インライン SVG (チャートライブラリ不使用) (2026-06-25)
//   ・HorizontalBreakevenGauge: 0 → 損益分岐点 → 売上 の横ゲージ。
//       黒字時 = 0〜分岐点(固定費を賄う部分・青グレー) + 分岐点〜売上(安全余裕・緑)。
//       赤字時 = 売上〜分岐点 を赤ハッチで「あと ¥X で黒字」を強調。
//   ・SafetyArcGauge: 安全余裕率の半円(180°)ゲージ。黒字圏=緑 / 赤字圏=赤。
//   配色: 黒字/安全=緑 #2da663, 赤字/危険=橙〜赤 #c4732a〜#dc2626, 分岐点=グレー破線 #6b7280。
// ---------------------------------------------------------------------------

import React from 'react';

const GREEN = '#2da663';
const GREEN_DK = '#1f8a4c';
const ORANGE = '#c4732a';
const RED = '#dc2626';
const GREY = '#6b7280';
const FIXED_BLUE = '#7d93b8';
const TEXT = '#3a3530';
const SUB = '#8a8580';

const yenShort = (v: number): string => {
  const a = Math.abs(v);
  const sign = v < 0 ? '▲' : '';
  if (a >= 1e8) return `${sign}¥${(a / 1e8).toFixed(a >= 1e9 ? 0 : 2)}億`;
  if (a >= 1e6) return `${sign}¥${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e4) return `${sign}¥${Math.round(a / 1e3)}千`;
  return `${sign}¥${Math.round(a).toLocaleString('ja-JP')}`;
};

export interface HorizontalGaugeProps {
  sales: number;
  breakeven: number;
  /** 市場家賃モードの破線で重ねる前年分岐点 (任意)。 */
  priorBreakeven?: number | null;
  /** 市場家賃モード時は紫アクセントで分岐点ラベルを着色。 */
  market?: boolean;
  height?: number;
}

/** 横ゲージ: 0 → 分岐点 → 売上。安全余裕(緑) / 不足分(赤)で塗り分け。 */
export const HorizontalBreakevenGauge: React.FC<HorizontalGaugeProps> = ({
  sales, breakeven, priorBreakeven, market, height = 132,
}) => {
  const W = 720;
  const H = height;
  const padL = 14;
  const padR = 14;
  const trackY = 52;
  const trackH = 34;
  const trackW = W - padL - padR;
  const max = Math.max(sales, breakeven, priorBreakeven || 0, 1) * 1.08;
  const x = (v: number) => padL + Math.max(0, Math.min(1, v / max)) * trackW;
  const profitable = sales >= breakeven;
  const beX = x(breakeven);
  const salesX = x(sales);
  const accent = market ? '#7c3aed' : GREY;
  const surplus = sales - breakeven;
  const surplusRatio = sales > 0 ? surplus / sales : 0;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
      aria-label={`売上 ${yenShort(sales)} / 損益分岐点 ${yenShort(breakeven)}`}
      style={{ fontVariantNumeric: 'tabular-nums', display: 'block' }}>
      <defs>
        <pattern id="be-hatch" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)">
          <rect width="7" height="7" fill={`${RED}1f`} />
          <line x1="0" y1="0" x2="0" y2="7" stroke={RED} strokeWidth="2.4" />
        </pattern>
        <linearGradient id="be-safe" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={GREEN} stopOpacity="0.75" />
          <stop offset="100%" stopColor={GREEN} stopOpacity="0.95" />
        </linearGradient>
      </defs>

      {/* track 背景 */}
      <rect x={padL} y={trackY} width={trackW} height={trackH} rx={7} fill="#efede9" />

      {/* 固定費を賄う部分 (0 → min(分岐点, 売上)) */}
      <rect x={padL} y={trackY} width={Math.max(0, Math.min(beX, salesX) - padL)} height={trackH}
        rx={7} fill={FIXED_BLUE} fillOpacity={0.5} />

      {profitable ? (
        // 安全余裕 (分岐点 → 売上) = 緑
        <rect x={beX} y={trackY} width={Math.max(0, salesX - beX)} height={trackH} rx={2} fill="url(#be-safe)" />
      ) : (
        // 赤字: 売上 → 分岐点 = 赤ハッチ
        <rect x={salesX} y={trackY} width={Math.max(0, beX - salesX)} height={trackH} rx={2} fill="url(#be-hatch)" />
      )}

      {/* 前年分岐点(任意・薄い破線) */}
      {priorBreakeven != null && priorBreakeven > 0 && (
        <g>
          <line x1={x(priorBreakeven)} y1={trackY - 6} x2={x(priorBreakeven)} y2={trackY + trackH + 6}
            stroke={SUB} strokeWidth={1.2} strokeDasharray="3 3" />
          <text x={x(priorBreakeven)} y={trackY + trackH + 20} fontSize={10} fill={SUB} textAnchor="middle">前年BEP</text>
        </g>
      )}

      {/* 損益分岐点ライン(グレー破線) */}
      <line x1={beX} y1={trackY - 16} x2={beX} y2={trackY + trackH + 10} stroke={accent} strokeWidth={1.6} strokeDasharray="5 4" />
      <text x={Math.min(beX, W - 90)} y={trackY - 21} fontSize={11.5} fontWeight={700} fill={accent} textAnchor="middle">
        損益分岐点 {yenShort(breakeven)}
      </text>

      {/* 売上マーカー(●) */}
      <circle cx={salesX} cy={trackY + trackH / 2} r={6.5} fill={profitable ? GREEN_DK : RED} stroke="#fff" strokeWidth={2} />
      <text x={Math.min(salesX, W - 40)} y={trackY + trackH + 30} fontSize={11.5} fontWeight={700}
        fill={profitable ? GREEN_DK : RED} textAnchor="middle">
        売上 {yenShort(sales)}
      </text>

      {/* 結論ラベル */}
      <text x={padL} y={26} fontSize={13} fontWeight={700} fill={profitable ? GREEN_DK : ORANGE}>
        {profitable
          ? `安全余裕 +${yenShort(surplus)} ／ ${(surplusRatio * 100).toFixed(1)}%`
          : `黒字まで あと ${yenShort(-surplus)}`}
      </text>

      {/* 凡例 */}
      <g transform={`translate(${W - 250}, ${H - 12})`} fontSize={10} fill={TEXT}>
        <rect x={0} y={-9} width={11} height={11} rx={2} fill={FIXED_BLUE} fillOpacity={0.5} />
        <text x={16} y={0}>固定費を賄う部分</text>
        <rect x={132} y={-9} width={11} height={11} rx={2} fill={GREEN} fillOpacity={0.85} />
        <text x={148} y={0}>{profitable ? '安全余裕' : '不足'}</text>
      </g>
    </svg>
  );
};

export interface SafetyArcProps {
  /** 安全余裕率 (小数 0.034 = 3.4%)。 */
  ratio: number | null;
  /** 限界利益率 (= 粗利益率, 小数)。 */
  marginRatio?: number | null;
  fixedCost?: number | null;
  market?: boolean;
}

/** 安全余裕率 半円(180°)ゲージ。針 + 塗り弧。 */
export const SafetyArcGauge: React.FC<SafetyArcProps> = ({ ratio, marginRatio, fixedCost, market }) => {
  const W = 240;
  const H = 158;
  const cx = W / 2;
  const cy = 124;
  const r = 96;
  const undef = ratio == null;
  // -50% .. +50% を 180°(左→右) にマップ。clamp。
  const clamp = Math.max(-0.5, Math.min(0.5, undef ? 0 : ratio));
  const t = (clamp + 0.5); // 0..1
  const angle = Math.PI - t * Math.PI; // π(左) → 0(右)
  const pt = (ang: number, rr: number) => ({ x: cx + rr * Math.cos(ang), y: cy - rr * Math.sin(ang) });
  const arc = (a0: number, a1: number, rr: number) => {
    const p0 = pt(a0, rr); const p1 = pt(a1, rr);
    const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0;
    const sweep = a1 < a0 ? 1 : 0;
    return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${rr} ${rr} 0 ${large} ${sweep} ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
  };
  const mid = Math.PI / 2; // 0% = 真上
  const profitable = !undef && ratio! >= 0;
  const valColor = undef ? SUB : profitable ? GREEN_DK : RED;
  const accent = market ? '#7c3aed' : valColor;
  const needle = pt(angle, r - 8);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
      aria-label={undef ? '安全余裕率 未確定' : `安全余裕率 ${(ratio! * 100).toFixed(1)}%`}
      style={{ fontVariantNumeric: 'tabular-nums', display: 'block' }}>
      {/* 背景弧 */}
      <path d={arc(Math.PI, 0, r)} fill="none" stroke="#e8e6e2" strokeWidth={14} strokeLinecap="round" />
      {/* 赤字側 (左半分: π → 真上) */}
      <path d={arc(Math.PI, mid, r)} fill="none" stroke={`${RED}55`} strokeWidth={14} strokeLinecap="round" />
      {/* 黒字側 (右半分: 真上 → 0) */}
      <path d={arc(mid, 0, r)} fill="none" stroke={`${GREEN}55`} strokeWidth={14} strokeLinecap="round" />
      {/* 現在値の塗り弧 (0% から現在へ) */}
      {!undef && (
        <path d={arc(mid, angle, r)} fill="none" stroke={profitable ? GREEN : RED} strokeWidth={14} strokeLinecap="round" />
      )}
      {/* 0% 目盛 */}
      <line x1={cx} y1={cy - r - 9} x2={cx} y2={cy - r + 9} stroke={SUB} strokeWidth={1.4} />
      {/* 針 */}
      {!undef && (
        <>
          <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke={accent} strokeWidth={3} strokeLinecap="round" />
          <circle cx={cx} cy={cy} r={5} fill={accent} />
        </>
      )}
      {/* 中央値 */}
      <text x={cx} y={cy - 16} fontSize={32} fontWeight={800} fill={valColor} textAnchor="middle">
        {undef ? '—' : `${(ratio! * 100).toFixed(1)}%`}
      </text>
      <text x={cx} y={cy + 6} fontSize={11.5} fontWeight={700} fill={undef ? SUB : profitable ? GREEN_DK : ORANGE} textAnchor="middle">
        {undef ? '未確定' : profitable ? '黒字圏' : '赤字圏'}
      </text>
      {/* 補助 */}
      <text x={cx} y={H - 6} fontSize={10.5} fill={SUB} textAnchor="middle">
        {marginRatio != null ? `限界利益率 ${(marginRatio * 100).toFixed(1)}%` : ''}
        {fixedCost != null ? `　固定費 ${yenShort(fixedCost)}` : ''}
      </text>
    </svg>
  );
};
